import React, { useEffect, useMemo, useState } from 'react';
import { DropdownMenu } from '../DropdownMenu';
import { ReachListVersionsHelper } from '../../../application-layer/compliance/ReachListVersionsHelper';
import { ReachListVersions, ReachListVersion } from '../../../application-layer/compliance/ReachListVersions';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

interface ComplianceResponseDateReachProps {
    value: string;
    onValueChange: (value: string) => void;
    localizer: IStringLocalizer;
    isDisabled?: boolean;
    surveyState?: number; // ResponseStateType: 0=InProgress, 1=CompleteAndSubmitted, 2=Canceled
}

// Placeholder sentinel value that displays as "Select an option" when persisted date is truncated
const PLACEHOLDER_VALUE = "__reach_placeholder__";

/**
 * REACH SVHC Reference Date Selector
 * 
 * State-based behavior:
 * - InProgress (0): Filtered dropdown (post-2021), shows placeholder + warning if old date persisted, outdated warning if not most recent
 * - Completed/Canceled (1/2): Full dropdown (all dates), no truncation handling, only outdated warning if applicable
 */
const ComplianceResponseDateReach: React.FC<ComplianceResponseDateReachProps> = ({
    value,
    onValueChange,
    localizer,
    isDisabled,
    surveyState
}) => {
    // State 0 = InProgress (show filtered list), else show all historical dates
    const isSurveyInProgress = surveyState === 0;
    
    // Conditional filtering: post-2021 dates for in-progress surveys, all dates otherwise
    const filteredVersions = useMemo(() => 
        isSurveyInProgress 
            ? ReachListVersionsHelper.getFilteredForSurvey() 
            : ReachListVersions.reachListVersions, 
        [isSurveyInProgress]
    );
    
    const optionValues = useMemo(() => filteredVersions.map((rv: ReachListVersion) => rv.date), [filteredVersions]);
    const [selectedDate, setSelectedDate] = useState<string | undefined>(value);
    
    // Derived state: Calculate if value is truncated (old date not in filtered dropdown for in-progress surveys)
    const truncatedPersistedValue = useMemo(() => {
        // Only consider truncation for in-progress surveys with a value that's not in the filtered list
        if (value && isSurveyInProgress && !optionValues.includes(value)) {
            return value;
        }
        return null;
    }, [value, isSurveyInProgress, optionValues]);

    // Sync effect: Handles value changes and updates selectedDate based on truncation state
    useEffect(() => {
        // Case 1: Value is valid (exists in current filtered options) - sync selectedDate
        if (value && optionValues.includes(value)) {
            if (selectedDate !== value) {
                setSelectedDate(value);
            }
            return;
        }

        // Case 2: In-progress survey + value is truncated - clear selectedDate (will show placeholder)
        if (value && isSurveyInProgress && !optionValues.includes(value)) {
            if (selectedDate !== undefined) {
                setSelectedDate(undefined);
            }
            return;
        }

        // Case 3: Not in-progress survey + value exists - treat as valid (no truncation in completed/canceled surveys)
        if (value && !isSurveyInProgress) {
            if (selectedDate !== value) {
                setSelectedDate(value);
            }
            return;
        }

        // Case 4: No value - clear selectedDate
        if (selectedDate !== undefined) {
            setSelectedDate(undefined);
        }
    }, [value, optionValues, isSurveyInProgress, selectedDate]);

    // Dropdown display: Show placeholder when in-progress with truncated value OR when no value exists (EXG5-645), otherwise show selected date
    const effectiveValue = (value && optionValues.includes(value)) ? value : selectedDate;
    const shouldShowPlaceholder = (isSurveyInProgress && truncatedPersistedValue !== null) || !value;
    const options = useMemo<string[]>(() => shouldShowPlaceholder ? [PLACEHOLDER_VALUE, ...optionValues] : optionValues, [shouldShowPlaceholder, optionValues]);
    const dropdownValue = (isSurveyInProgress && truncatedPersistedValue !== null) || !value ? PLACEHOLDER_VALUE : effectiveValue;

    // Handle user selection: Prevent selecting placeholder, update value on valid selection
    const handleDateChange = (newDate: string) => {
        // User selected placeholder option - prevent selection if truncated value exists
        if (newDate === PLACEHOLDER_VALUE) {
            if (truncatedPersistedValue !== null) {
                return; // Don't allow placeholder selection when showing truncated date warning
            }
            
            // Otherwise, clear the current selection
            if (selectedDate || value) {
                setSelectedDate(undefined);
                onValueChange("");
            }
            return;
        }

        // User selected a valid REACH date - update state and notify parent (truncation auto-clears via derived state)
        if (newDate !== value && newDate !== selectedDate) {
            setSelectedDate(newDate);
            onValueChange(newDate);
        }
    };

    // Format dropdown options: "YYYY-MM-DD (XXX Substances)" or "Select an option" for placeholder
    const mapReachListVersions = (date: string) => {
        if (date === PLACEHOLDER_VALUE) {
            return localizer.localizeString("Select an option");
        }

        const version = filteredVersions.find((rv: ReachListVersion) => rv.date === date);
        if (!version) {
            return date;
        }

        return `${version.date} (${version.count} ${localizer.localizeString("Substances")})`;
    };

    return (
        <div className="compliance-response-date-reach-container">
            <DropdownMenu
                options={options}
                preselectedOption={dropdownValue}
                onOptionSelected={handleDateChange}
                getFriendlyNameForOptions={mapReachListVersions}
                getUniqueValueForOption={o => o}
                value={dropdownValue}
                isDisabled={isDisabled}
            />
            
            {/* Warning display: In-progress shows truncation OR outdated warning; Completed/Canceled shows only outdated warning */}
            {isSurveyInProgress ? (
                (truncatedPersistedValue || (selectedDate && !ReachListVersionsHelper.isMostRecent(selectedDate))) && (
                    <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <span style={{ marginRight: '8px', fontSize: '18px' }}>⚠️</span>
                            <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                    {truncatedPersistedValue
                                        ? localizer.localizeString("Persisted Reference Date No Longer Available")
                                        : localizer.localizeString("This version is outdated")}
                                </div>
                                <div>
                                    {truncatedPersistedValue
                                        ? `${localizer.localizeString("Your previously selected Reference Date")} (${truncatedPersistedValue}) ${localizer.localizeString("is from an older Candidate List version that is no longer available. Please select a current Reference Date from the available options.")}`
                                        : localizer.localizeString("Under REACH Article 33, compliance must be based on the most recent Candidate List of SVHCs, as published by the European Chemicals Agency (ECHA). Please use the comment field below for further explanation.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                (selectedDate && !ReachListVersionsHelper.isMostRecent(selectedDate)) && (
                    <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <span style={{ marginRight: '8px', fontSize: '18px' }}>⚠️</span>
                            <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                    {localizer.localizeString("This version is outdated")}
                                </div>
                                <div>
                                    {localizer.localizeString("Under REACH Article 33, compliance must be based on the most recent Candidate List of SVHCs, as published by the European Chemicals Agency (ECHA). Please use the comment field below for further explanation.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

export default ComplianceResponseDateReach;